import React, { useState } from 'react';
import './burger-menu.css';
import { useLanguage } from './context/LanguageContext.tsx';

interface BurgerMenuProps {
  selectedProduct: string;
  selectedYear: number;
  selectedDays: number;
  temperatureThreshold: number;
  checkedItems: {
    vegetationSeason?: boolean;
    vegetationSeasonEnd?: boolean;
    seasonLength?: boolean;
  };
  language: string;
  selectedOption: string | null;
  setSelectedOption: (value: string | null) => void;
}

const BurgerMenu: React.FC<BurgerMenuProps> = ({
  selectedProduct,
  selectedYear,
  selectedDays,
  temperatureThreshold,
  checkedItems,
  selectedOption,
  setSelectedOption
}) => {
  const [open, setOpen] = useState(false);
  const { language } = useLanguage();

  const translations = {
    cz: {
      menu: {
        methods: 'Popis metod',
        about: 'O aplikaci',
        download: 'Stáhnout',
      },
      panels: {
        methodsTitle: 'Popis metod',
        methodsText: [
          {
            title: 'Kombinovaný produkt',
            text: 'Tato inovativní metoda spojuje přesnost pozemního měření a plošné pokrytí dálkového průzkumu Země. Klíčovou myšlenkou je využití přesných dat z pozemních PAR senzorů ke kalibraci plošných satelitních dat. Nejprve byl na lokalitách sítě DendroNetwork, kde jsou k dispozici oba typy měření, vytvořen matematický model, který popisuje vztah mezi satelitním vegetačním indexem a skutečným množstvím světla pronikajícím pod koruny stromů. Tento model byl následně aplikován na celoplošná satelitní data pro celé území ČR. Tím je satelitní index EVI transformován na hodnotu, která simuluje reálná pozemní měření. Z této nové, kalibrované datové řady jsou poté odhadnuty fenologické indikátory pro regiony ČR. Výsledkem je robustní a plošně konzistentní produkt, který umožňuje mapovat fenologii lesů i v místech bez přímého pozemního měření.'
          },
          {
            title: 'Dálkový průzkum Země',
            text: 'Tato metoda je založena výhradně na analýze časových řad vegetačního indexu EVI z družicových dat produktu Harmonised Landsat Sentinel-2. Díky kombinaci dat z více satelitů je k dispozici hustá časová řada snímků pro přesné sledování vegetace. Pro každý region je nejprve vytvořena modelová křivka průměrného vegetačního cyklu na základě dat z předchozích let. Data z aktuálního roku jsou následně porovnána s tímto dlouhodobým modelem. Tímto krokem jsou automaticky odfiltrovány nepravděpodobné hodnoty způsobené například oblačností nebo sněhem. Na očištěná data je aplikován vyhlazovací filtr, aby byla získána plynulá a spojitá křivka aktuálního průběhu vegetace. Začátek a konec sezóny jsou určeny pomocí dynamických prahových hodnot, které se odvíjejí od charakteristik dlouhodobé průměrné křivky. Tím je zajištěna robustnost metody vůči meziročním výkyvům.'
          },
          {
            title: 'Meteorologická data',
            intro: 'Jedná se o tradiční klimatologický přístup, který definuje vegetační období na základě průměrných denních teplot vzduchu. Tento způsob je standardně využíván pro vymezení podmínek vhodných pro růst rostlin. Vegetační období a jeho dílčí fáze jsou definovány překročením klíčových teplotních prahů po určitý počet po sobě jdoucích dní:',
            bullets: [
              'Velké vegetační období: Začíná, jakmile průměrná denní teplota po dobu 5 dnů překročí 5 °C.',
              'Hlavní vegetační období: Je vymezeno trvalým překročením teploty 10 °C.',
              'Vegetační léto: Nastává, když průměrná denní teplota po dobu 5 dnů překročí 15 °C.'
            ],
            outro: 'Konec vegetačního období je vymezen analogicky, a to poklesem teploty pod definované hranice. Tato metoda poskytuje základní klimatologický kontext a umožňuje sledovat posuny vegetačních období vlivem změn teploty.'
          },
          {
            title: 'PAR Senzory',
            text: 'Tato metoda využívá data z pozemních senzorů, které měří množství fotosynteticky aktivního záření (PAR) pronikajícího pod koruny stromů. Jde o přímé měření stavu olistění listnatých porostů. Denní měření ze senzorů jsou nejprve normalizována, aby byl odstraněn vliv počasí (střídání slunečných a zatažených dnů). Takto je získán údaj o tom, jaká část slunečního záření je pohlcena korunami stromů. Tato normalizovaná časová řada je modelována pomocí dvojité logistické funkce, která přesně popisuje postupný nárůst listové plochy na jaře a její pokles na podzim. Z tohoto matematického modelu jsou následně určeny klíčové fenologické indikátory. Jsou jimi dny, kdy první derivace funkce dosahuje svého maxima (nejrychlejší nárůst olistění) a minima (nejrychlejší opad listí). Tento přístup poskytuje vysoce přesné a časově detailní informace o reálném fyziologickém stavu lesa na dané lokalitě.'
          },
        ],
        aboutTitle: 'O aplikaci',
        aboutText: 'Tato aplikace zobrazuje mapové vrstvy a sezónní data.',
        downloadTitle: 'Stáhnout aktuální data',
        downloadDesc: 'Stáhne CSV odpovídající aktuálně zvoleným parametrům (produkt, rok, typ sezony) pro ORP.',
        close: 'Zavřít',
        downloadBtn: 'Stáhnout CSV',
        alertSelect: 'Vyberte prosím typ sezony (začátek/konec/délka).',
        alertPAR: 'Pro PAR senzory není stahování dat k dispozici.',
        errorDownload: 'Nepodařilo se stáhnout soubor.',
        errorLength: 'Nepodařilo se vygenerovat délku sezony.',
      },
    },
    en: {
      menu: {
        methods: 'Method Description',
        about: 'About App',
        download: 'Download',
      },
      panels: {
        methodsTitle: 'Method Description',
        methodsText: [
          {
            title: 'Combined Product',
            text: 'This innovative method combines the precision of ground measurements with the spatial coverage of remote sensing. The key idea is to use precise data from ground-based PAR sensors to calibrate area-wide satellite data. First, a mathematical model was created at the DendroNetwork sites, where both types of measurements are available. This model describes the relationship between the satellite vegetation index and the actual amount of light penetrating the tree canopy. The model was subsequently applied to area-wide satellite data for the entire territory of the Czech Republic. This transforms the satellite EVI index into a value that simulates real ground measurements. From this new, calibrated data series, phenological indicators are then estimated for the regions of the Czech Republic. The result is a robust and spatially consistent product that allows for mapping forest phenology even in locations without direct ground measurements.'
          },
          {
            title: 'Remote Sensing',
            text: "This method is based exclusively on the analysis of time series of the EVI vegetation index from the Harmonised Landsat Sentinel-2 satellite data product. Thanks to the combination of data from multiple satellites, a dense time series of images is available for precise vegetation monitoring. For each region, a model curve of the average vegetation cycle is first created based on data from previous years. Data from the current year are then compared with this long-term model. This step automatically filters out improbable values caused, for example, by cloud cover or snow. A smoothing filter is applied to the cleaned data to obtain a smooth and continuous curve of the current vegetation trend. The start and end of the season are determined using dynamic thresholds, which are derived from the characteristics of the long-term average curve. This ensures the method's robustness against inter-annual variations."
          },
          {
            title: 'Meteorological data',
            intro: 'This is a traditional climatological approach that defines the growing season based on average daily air temperatures. This method is standardly used to define conditions suitable for plant growth. The growing season and its sub-phases are defined by exceeding key temperature thresholds for a certain number of consecutive days:',
            bullets: [
              'Large growing season: Begins once the average daily temperature exceeds 5 °C for 5 days.',
              'Main growing season: Is defined by a sustained temperature increase above 10 °C.',
              'Vegetative summer: Occurs when the average daily temperature exceeds 15 °C for 5 days.'
            ],
            outro: 'The end of the growing season is defined analogously, by the temperature dropping below the defined limits. This method provides a basic climatological context and allows for monitoring shifts in the growing seasons due to temperature changes.'
          },
          {
            title: 'PAR Sensors',
            text: "This method uses data from ground-based sensors that measure the amount of Photosynthetically Active Radiation (PAR) penetrating the tree canopy. This is a direct measurement of the leafing status of deciduous stands. Daily measurements from the sensors are first normalized to remove the influence of weather (alternation of sunny and cloudy days). This provides a measure of what portion of solar radiation is absorbed by the tree crowns. This normalized time series is modeled using a double logistic function, which accurately describes the gradual increase of leaf area in the spring and its decline in the autumn. From this mathematical model, the key phenological indicators are then determined. These are the days when the function's first derivative reaches its maximum (the fastest increase in leaf cover) and minimum (the fastest leaf fall). This approach provides highly precise and temporally detailed information about the real physiological state of the forest at a given site."
          },
        ],
        aboutTitle: 'About Application',
        aboutText: 'This app displays map layers and seasonal data.',
        downloadTitle: 'Download Current Data',
        downloadDesc: 'Downloads CSV according to currently selected parameters (product, year, season type) for ORP.',
        close: 'Close',
        downloadBtn: 'Download CSV',
        alertSelect: 'Please select season type (start/end/length).',
        alertPAR: 'Data download is not available for PAR sensors.',
        errorDownload: 'Failed to download file.',
        errorLength: 'Failed to generate season length.',
      },
    },
  }[language];

  const handleSelect = (option: string) => {
    setOpen(false);
    setSelectedOption(option); // from props
  };

  const handleClosePanel = () => setSelectedOption(null);

  const handleDownload = async () => {
    if (selectedProduct === 'parSensors') {
      alert(translations.panels.alertPAR);
      return;
    }

    let path = '';
    let filename = '';

    const seasonType = checkedItems.vegetationSeason
      ? 'start'
      : checkedItems.vegetationSeasonEnd
        ? 'end'
        : checkedItems.seasonLength
          ? 'length'
          : null;

    if (!seasonType) {
      alert(translations.panels.alertSelect);
      return;
    }

    const suffix = seasonType === 'start' ? 'sos' : seasonType === 'end' ? 'eos' : 'los';

    // If length of season needs to be computed
    if (seasonType === 'length') {
      let startPath = '';
      let endPath = '';

      if (selectedProduct === 'dpz') {
        startPath = `/remote_sensing_orp/start_of_season/${selectedYear}.csv`;
        endPath = `/remote_sensing_orp/end_of_season/${selectedYear}.csv`;
        filename = `so_orp_rs_${selectedYear}_los.csv`;
      } else if (selectedProduct === 'combinedProduct') {
        startPath = `/combined/start_of_season/${selectedYear}.csv`;
        endPath = `/combined/end_of_season/${selectedYear}.csv`;
        filename = `so_orp_combined_${selectedYear}_los.csv`;
      } else {
        const thresholdStr = `${selectedDays}d_${temperatureThreshold}deg`;
        startPath = `/temperature_orp/start_of_season/${selectedYear}/so_orp_temp_${selectedYear}_sos_${thresholdStr}.csv`;
        endPath = `/temperature_orp/end_of_season/${selectedYear}/so_orp_temp_${selectedYear}_eos_${thresholdStr}.csv`;
        filename = `so_orp_temp_${selectedYear}_los_${thresholdStr}.csv`;
      }

      try {
        const [startText, endText] = await Promise.all([
          fetch(startPath).then(res => res.ok ? res.text() : Promise.reject()),
          fetch(endPath).then(res => res.ok ? res.text() : Promise.reject()),
        ]);

        const parseCSV = (csv: string): Record<string, number | null> => {
          const lines = csv.trim().split('\n').slice(1);
          const result: Record<string, number | null> = {};
          for (const line of lines) {
            const [site, doy] = line.split(',').map(s => s.replace(/^"|"$/g, '').trim());
            const value = doy.toUpperCase() === 'NA' ? null : parseInt(doy, 10);
            result[site] = isNaN(value!) ? null : value;
          }
          return result;
        };

        const startMap = parseCSV(startText);
        const endMap = parseCSV(endText);

        const allSites = new Set([...Object.keys(startMap), ...Object.keys(endMap)]);

        const rows = ['site,length'];
        allSites.forEach(site => {
          const start = startMap[site];
          const end = endMap[site];
          const length = start != null && end != null ? end - start : 'NA';
          rows.push(`${site},${length}`);
        });

        const csvContent = rows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        a.remove();
      } catch {
        alert(translations.panels.errorLength);
      }

      return;
    }

    // Standard download
    if (selectedProduct === 'dpz') {
      path = `/remote_sensing_orp/${seasonType}_of_season/${selectedYear}.csv`;
      filename = `so_orp_rs_${selectedYear}_${suffix}.csv`;
    } else if (selectedProduct === 'combinedProduct') {
      path = `/combined/${seasonType}_of_season/${selectedYear}.csv`;
      filename = `so_orp_combined_${selectedYear}_${suffix}.csv`;
    } else {
      const folder = `temperature_orp/${seasonType}_of_season/${selectedYear}`;
      const thresholdStr = `${selectedDays}d_${temperatureThreshold}deg`;
      path = `/${folder}/so_orp_temp_${selectedYear}_${suffix}_${thresholdStr}.csv`;
      filename = `so_orp_temp_${selectedYear}_${suffix}_${thresholdStr}.csv`;
    }

    fetch(path)
      .then(res => {
        if (!res.ok) throw new Error();
        return res.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        a.remove();
      })
      .catch(() => alert(translations.panels.errorDownload));
  };

  return (
    <div className="burger-menu">
      <button className="burger-button" onClick={() => setOpen(!open)}>☰</button>

      {open && (
        <div className="burger-dropdown">
          <ul>
            <li onClick={() => handleSelect('settings')}>{translations.menu.methods}</li>
            <li onClick={() => handleSelect('about')}>{translations.menu.about}</li>
            <li onClick={() => handleSelect('download')}>{translations.menu.download}</li>
          </ul>
        </div>
      )}

      {selectedOption === 'settings' && (
        <div className="panel-overlay">
          <div className="panel-content">
            <h2>{translations.panels.methodsTitle}</h2>
            <p>
              {translations.panels.methodsText.map((method, idx) => (
                <div key={idx} style={{ marginBottom: '1.5em', textAlign: 'left' }}>
                  <h3 style={{ marginBottom: '0.5em' }}>{method.title}</h3>

                  {/* Ak existuje štruktúrovaný obsah (intro / bullets / outro) */}
                  {method.intro || method.bullets || method.outro ? (
                    <>
                      {method.intro && <p>{method.intro}</p>}

                      {method.bullets && (
                        <ul style={{ paddingLeft: '1.2em' }}>
                          {method.bullets.map((item, i) => (
                            <li key={i} style={{ marginBottom: '0.4em' }}>{item}</li>
                          ))}
                        </ul>
                      )}

                      {method.outro && <p>{method.outro}</p>}
                    </>
                  ) : (
                    // Fallback pre metódy, ktoré majú len `text`
                    method.text && <p>{method.text}</p>
                  )}
                </div>
              ))}

            </p>
            <button onClick={handleClosePanel}>{translations.panels.close}</button>
          </div>
        </div>
      )}

      {selectedOption === 'about' && (
        <div className="panel-overlay">
          <div className="panel-content">
            <h2>{translations.panels.aboutTitle}</h2>
            <p>{translations.panels.aboutText}</p>
            <p>CzechGlobe &copy; 2025</p>
            <button onClick={handleClosePanel}>{translations.panels.close}</button>
          </div>
        </div>
      )}

      {selectedOption === 'download' && (
        <div className="panel">
          <h2>{translations.panels.downloadTitle}</h2>
          <p>{translations.panels.downloadDesc}</p>
          <button onClick={handleDownload}>{translations.panels.downloadBtn}</button>
          <button onClick={handleClosePanel}>{translations.panels.close}</button>
        </div>
      )}
    </div>
  );
};

export default BurgerMenu;
